import { Box, Grid, Card, SxProps, Skeleton } from "@mui/material";
import { selectBooking } from "../../../bookingSlice";
import SuccessSection from "../SuccessSection";
import { useClientPortalSelector } from "../../../../../redux";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";

import { StripeProvider, useInitializeStripe } from "../../../../Shared/Stripe";
import ConfirmationDetailsWithPayment from "./ConfirmationDetailsWithPayment";
import TermsAndConditionsLink from "../../Shared/TermsAndConditionsLink";
import TermsAndConditionsContent from "../../Shared/TermsAndConditionsContent";
import { useCalculateStripeSurcharge } from "../useCalculateStripeSurcharge";
import ConfirmationPayment from "./ConfirmationPayment";
import { AppointmentBookingClientModel, StripeSurcharge, TaxUsageType } from "types/generated/models";
import { ConfirmBookingFunction } from "../../../hooks/useBookingConfirmation";
import { applyTax } from "utils/taxHelper";
import { useClientDiscount } from "../useClientDiscount";

import styles from "./ConfirmationWithPayment.module.css";

export interface ConfirmationWithPaymentProps {
    confirmBooking: ConfirmBookingFunction;
    bookedSuccessfully: boolean;
    paymentRequired: boolean;
    paymentOptional: boolean;
    shouldShowContactsDropdown: boolean;
    relatedClients: AppointmentBookingClientModel[];
}

const termsAndConditionsLinkWrapperSx: SxProps = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
    minHeight: "35px",
};

export default function ConfirmationWithPayment({
    confirmBooking,
    bookedSuccessfully,
    paymentRequired,
    paymentOptional,
    shouldShowContactsDropdown,
    relatedClients,
}: ConfirmationWithPaymentProps) {
    const { session, provider, service } = useClientPortalSelector(selectBooking);

    const [termsAndConditionsVisibility, setTermsAndConditionsVisibility] = useState<boolean>(false);
    const [stripeSurcharge, setStripeSurcharge] = useState<StripeSurcharge | null>(null);
    const [stripeSurchargeFullAmount, setStripeSurchargeFullAmount] = useState<StripeSurcharge | null>(null);
    const {
        clientPortalConfig: { stripeKey, taxUsage },
    } = useClientPortalConfigContext();

    const { discountContent, applyDiscountToAmount } = useClientDiscount();

    const discountedFullAmount = useMemo(
        () => (service?.data?.amount ? applyDiscountToAmount(service.data.amount) : 0),
        [service?.data?.amount, applyDiscountToAmount]
    );

    const discountedDepositAmount = useMemo(() => {
        if (!service?.data?.needsDeposit || !service?.data?.portalDeposit) {
            return 0;
        }
        return Math.min(service.data.portalDeposit, discountedFullAmount);
    }, [service?.data?.needsDeposit, service?.data?.portalDeposit, discountedFullAmount]);

    const { stripe, loading: stripeLoading } = useInitializeStripe({
        stripeKey: stripeKey,
        practitionerStripeAccountId: provider.data.providerStripeAccount,
    });

    const { isLoading: stripeSurchargeLoading, calculateStripeSurcharge } = useCalculateStripeSurcharge();

    const completedData = session.completedBookingData;

    const getAmountWithTax = useCallback(
        (amount: number): number => {
            return applyTax(taxUsage || TaxUsageType.NoTax, amount, service.data?.taxRate);
        },
        [taxUsage, service.data?.taxRate]
    );

    useEffect(() => {
        if (service?.data?.amount && service?.data?.serviceId) {
            const loadStripeSurcharges = async () => {
                const baseAmount =
                    service.data.needsDeposit && service.data.portalDeposit
                        ? discountedDepositAmount
                        : discountedFullAmount;

                const amountToCalculate = getAmountWithTax(baseAmount);

                const result = await calculateStripeSurcharge({
                    serviceId: service.data.serviceId,
                    amount: amountToCalculate,
                });
                setStripeSurcharge(result);

                // Calculate full amount surcharge for "Pay full amount" button
                if (service.data.needsDeposit && paymentOptional) {
                    const fullAmountWithTax = getAmountWithTax(discountedFullAmount);
                    const fullAmountResult = await calculateStripeSurcharge({
                        serviceId: service.data.serviceId,
                        amount: fullAmountWithTax,
                    });
                    setStripeSurchargeFullAmount(fullAmountResult);
                } else {
                    setStripeSurchargeFullAmount(null);
                }
            };
            void loadStripeSurcharges();
        }
    }, [
        service,
        calculateStripeSurcharge,
        getAmountWithTax,
        paymentOptional,
        discountedFullAmount,
        discountedDepositAmount,
    ]);

    return (
        <Box className={styles.confirmationDetailsWrapper}>
            <Grid container spacing={3} sx={{ width: "100%" }}>
                <ConfirmationDetailsWithPayment
                    shouldShowContactsDropdown={shouldShowContactsDropdown}
                    relatedClients={relatedClients}
                    bookedSuccessfully={bookedSuccessfully}
                />

                <Grid
                    sx={{ width: "100%" }}
                    size={{
                        xs: 12,
                        md: 6,
                    }}
                >
                    {bookedSuccessfully ? (
                        <SuccessSection
                            paymentData={completedData?.payment}
                            appointmentId={completedData?.availability?.timeEntry?.appointmentId}
                        />
                    ) : stripeLoading || stripeSurchargeLoading ? (
                        <Skeleton
                            data-test-id="skeleton_confirmation_with_payment"
                            variant="rounded"
                            sx={{ height: "200px" }}
                        ></Skeleton>
                    ) : (
                        <Card sx={{ padding: "20px" }}>
                            <StripeProvider stripe={stripe}>
                                {stripeSurcharge && (
                                    <ConfirmationPayment
                                        totalAmount={stripeSurcharge.totalAmount}
                                        totalFee={stripeSurcharge.totalFee}
                                        confirmBooking={confirmBooking}
                                        paymentRequired={paymentRequired}
                                        paymentOptional={paymentOptional}
                                        stripeSurchargeFullAmount={stripeSurchargeFullAmount}
                                        discountContent={service?.data?.needsDeposit ? null : discountContent}
                                        discountedFullAmount={discountedFullAmount}
                                        discountedDepositAmount={discountedDepositAmount}
                                        getAmountWithTax={getAmountWithTax}
                                    />
                                )}
                            </StripeProvider>
                            <Box data-test-id="section_terms_and_conditions">
                                <TermsAndConditionsLink
                                    wrapperSx={termsAndConditionsLinkWrapperSx}
                                    setTermsAndConditionsVisibility={setTermsAndConditionsVisibility}
                                    termsAndConditionsVisibility={termsAndConditionsVisibility}
                                />
                                <TermsAndConditionsContent
                                    termsAndConditionsVisibility={termsAndConditionsVisibility}
                                />
                            </Box>
                        </Card>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
